import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Navbar } from '../components';
import { paymentsAPI } from '../services/api';
import './PlanSelectionPage.css';

const PLANS = [
  {
    id: 20,
    interval: 'monthly',
    label: 'Monthly',
    price: '$19.99',
    period: '/month',
    badge: null,
    tagline: 'Full access, billed monthly.',
  },
  {
    id: 21,
    interval: 'yearly',
    label: 'Annual',
    price: '$149.99',
    period: '/year',
    badge: 'Best Value',
    tagline: 'Save over 37% — billed once a year.',
  },
];

const PlanSelectionPage = () => {
  const navigate = useNavigate();
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [firstMonthDiscount, setFirstMonthDiscount] = useState(false);
  // Holds cleanup logic (remove message listener + clear poll interval) for the active popup
  const cleanupRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/signin', { replace: true });
      return;
    }

    // If user already has an active subscription, skip to chat
    const checkSub = async () => {
      try {
        const apiBase = (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
        const res = await fetch(`${apiBase}/api/addons/my-plan`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          navigate('/chat', { replace: true });
          return;
        }
        if (res.status === 401 || res.status === 403) {
          // Token expired or invalid — send back to sign in
          localStorage.removeItem('access_token');
          navigate('/signin', { replace: true });
          return;
        }
        // 404 = no active subscription → show plan selection
      } catch (_) { }
      // Apply first-month discount if the banner set the flag this session
      if (sessionStorage.getItem('first_month_discount_active') === 'true') {
        setFirstMonthDiscount(true);
      }
      setCheckingSubscription(false);
    };
    checkSub();
  }, [navigate]);

  // Block browser back/forward — user must complete payment to proceed
  useEffect(() => {
    const onPopState = () => {
      window.history.pushState(null, '', window.location.pathname);
    };
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Clean up popup listeners when the component unmounts
  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  const handleSelectPlan = async (plan) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/signin', { replace: true });
      return;
    }

    setLoadingPlanId(plan.id);
    const isMonthlyDiscounted = firstMonthDiscount && plan.interval === 'monthly';
    const isAnnualDiscounted = firstMonthDiscount && plan.interval === 'yearly';

    // Open blank popup NOW — synchronous, inside the user gesture — before any await.
    // Browsers only honour window.open() without blocking when called directly from a click.
    const popupWidth = 500;
    const popupHeight = 700;
    const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
    const popup = window.open(
      'about:blank',
      'PayPal',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    const popupBlocked = !popup || popup.closed || typeof popup.closed === 'undefined';

    try {
      const res = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: plan.id,
        plan_interval: plan.interval,
        first_month_discount: isMonthlyDiscounted,
        annual_discount: isAnnualDiscounted,
      });

      const data = res?.data || res;
      const approveUrl = data?.approve_url;
      const orderId = data?.order_id || null;
      if (!approveUrl) {
        if (!popupBlocked) popup.close();
        toast.error('Could not start payment. Please try again.');
        setLoadingPlanId(null);
        return;
      }

      // Store checkout context so PaymentSuccessPage can capture the order.
      // context: 'popup' tells PaymentSuccessPage to post a message back and close.
      const storedInvite = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
      const isInvitee = !!(storedInvite.avatar_id || storedInvite.invite_id);
      const started_from = isInvitee ? '/chat' : '/create-avatar';
      const ctx = {
        order_id: orderId,
        avatar_plan_id: plan.id,
        plan_interval: plan.interval,
        auto_renew: true,
        started_from,
        context: 'popup',
        first_month_discount: isMonthlyDiscounted,
        annual_discount: isAnnualDiscounted,
      };
      sessionStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));
      localStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));

      // Popup was blocked even for about:blank — fall back to full-page redirect
      if (popupBlocked || popup.closed) {
        window.location.href = approveUrl;
        return;
      }

      // Navigate the already-open blank popup to the real PayPal URL
      popup.location.href = approveUrl;
      setPopupOpen(true);

      // ── Message handler ──────────────────────────────────────────────────
      // PaymentSuccessPage posts PAYPAL_SUCCESS or PAYPAL_ERROR then closes itself.
      const messageHandler = (event) => {
        if (event.data?.type === 'PAYPAL_SUCCESS') {
          if (cleanupRef.current) cleanupRef.current();
          try {
            sessionStorage.removeItem('user_fetched');
            sessionStorage.removeItem('user_data');
          } catch (_) {}
          const storedInvite = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
          const isInvitee = !!(storedInvite.avatar_id || storedInvite.invite_id);
          navigate(isInvitee ? '/chat' : '/create-avatar', { replace: true });
        } else if (event.data?.type === 'PAYPAL_ERROR') {
          if (cleanupRef.current) cleanupRef.current();
          toast.error('Payment failed. Please try again.');
          setLoadingPlanId(null);
          setPopupOpen(false);
        }
      };

      // ── Poll for manual popup closure (user cancelled / closed the window) ──
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          if (cleanupRef.current) cleanupRef.current();
          setLoadingPlanId(null);
          setPopupOpen(false);
        }
      }, 600);

      cleanupRef.current = () => {
        clearInterval(pollTimer);
        window.removeEventListener('message', messageHandler);
        cleanupRef.current = null;
      };

      window.addEventListener('message', messageHandler);
    } catch (err) {
      if (!popupBlocked) popup.close();
      console.error('PayPal create order failed:', err);
      if (err?.status === 401 || err?.statusCode === 401) {
        localStorage.removeItem('access_token');
        navigate('/signin', { replace: true });
        return;
      }
      toast.error(err?.message || 'Payment initiation failed. Please try again.');
      setLoadingPlanId(null);
    }
  };

  if (checkingSubscription) {
    return (
      <div className="plan-selection-page">
        <Navbar disabled={true} variant="transparent" />
        <div className="plan-selection-loading">
          <div className="spinner-border-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="plan-selection-page">
      <Navbar disabled={true} variant="transparent" />

      <main className="plan-selection-main">
        <div className="plan-selection-container">
          <div className="plan-cards">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card${plan.badge ? ' plan-card--featured' : ''}`}
              >
                {plan.badge && (
                  <div className="plan-card__badge">{plan.badge}</div>
                )}
                <div className="plan-card__label">{plan.label}</div>
                <div className="plan-card__price">
                  {((firstMonthDiscount && plan.interval === 'monthly') || (firstMonthDiscount && plan.interval === 'yearly')) && (
                    <span className="plan-card__price-original">{plan.price}</span>
                  )}
                  <span className="plan-card__price-amount">
                    {firstMonthDiscount && plan.interval === 'monthly'
                      ? '$9.99'
                      : firstMonthDiscount && plan.interval === 'yearly'
                        ? '$119.99'
                        : plan.price}
                  </span>
                  <span className="plan-card__price-period">{plan.period}</span>
                </div>
                {firstMonthDiscount && plan.interval === 'monthly' ? (
                  <p className="plan-card__tagline plan-card__tagline--discount">your first month</p>
                ) : firstMonthDiscount && plan.interval === 'yearly' ? (
                  <p className="plan-card__tagline plan-card__tagline--discount">Save 50% vs monthly — billed once a year</p>
                ) : (
                  <p className="plan-card__tagline">{plan.tagline}</p>
                )}

                <button
                  className="plan-card__cta"
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loadingPlanId !== null}
                >
                  {loadingPlanId === plan.id
                    ? (popupOpen ? 'Complete payment in popup…' : 'Starting…')
                    : 'Get Started'}
                </button>
              </div>
            ))}
          </div>

          <ul className="plan-shared-features">
            <li>Invite friends and family to connect with the Avatar you create</li>
            <li>Create multiple Avatars within one Family Group</li>
            <li>Add new documents and memories at any time</li>
            <li>Secure, private storage for everything you share</li>
          </ul>

          {popupOpen && (
            <p className="plan-selection-popup-hint">
              A PayPal window has opened — complete your payment there to continue.
            </p>
          )}

          <p className="plan-selection-footer">
            Payments are processed securely via PayPal. You can cancel anytime.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PlanSelectionPage;
